"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  compareR,
  divR,
  equalsR,
  formatRationalNice,
  intR,
  isZeroR,
  type Rational,
  signR,
} from "@/lib/tool-kit/math/rational";
import {
  digitCount,
  firstTerms,
  fitTwoTerms,
  infiniteSum,
  MAX_N,
  nthTerm,
  partialSum,
  positionOf,
  type RatioFit,
  scientific,
} from "@/lib/tool-kit/number-theory/geometric-sequence";
import {
  type RationalRead,
  readCountField,
  readRationalField,
} from "@/lib/tool-kit/number-theory/read";

/** English strings. A translated content file overrides these through `ui`. */
export const GEOMETRIC_SEQUENCE_CALCULATOR_STRINGS = {
  mode: "What do you know?",
  modeFirst: "First term and common ratio",
  modeTwo: "Two terms of the sequence",
  modePosition: "Find where a value sits",
  firstTerm: "First term, a₁",
  ratio: "Common ratio, r",
  ratioHint: "Multiply by this each time. Negative makes the signs alternate.",
  termNumber: "Term number, n",
  termNumberHint: "A whole number from 1 to {max}.",
  positionP: "Position of the first known term",
  valueP: "Its value",
  positionQ: "Position of the second known term",
  valueQ: "Its value",
  target: "Value to look for",
  results: "Results",
  nthTermCard: "Term a{ns}",
  sumCard: "Sum S{ns} of the first {n} terms",
  infiniteCard: "Sum of all terms, S∞",
  diverges: "No finite sum",
  ratioCard: "Common ratio",
  firstCard: "First term",
  formulaHeading: "General formula",
  generalTerm: "aₙ = {a1} · {rb}^(n − 1)",
  generalTermUnit: "aₙ = {rb}^(n − 1)",
  stepsHeading: "Working",
  stepRatio: "r^({q} − {p}) = a{qs} ÷ a{ps} = {y} ÷ {x} = {quotient}",
  stepRatioResult: "r = {r}",
  stepFirst: "a₁ = a{ps} ÷ r^({p} − 1) = {x} ÷ {rb}^({p} − 1) = {a1}",
  stepTerm: "a{ns} = a₁ · r^(n − 1) = {a1} · {rb}^({n} − 1) = {an}",
  stepSum:
    "S{ns} = a₁(1 − r^n) ÷ (1 − r) = {a1} × (1 − {rb}^{n}) ÷ (1 − {rb}) = {sum}",
  stepSumOne: "r = 1, so S{ns} = n × a₁ = {n} × {a1} = {sum}",
  stepInfinite: "S∞ = a₁ ÷ (1 − r) = {a1} ÷ (1 − {rb}) = {inf}",
  termsHeading: "The sequence",
  termsEllipsis: "…",
  growing: "|r| is greater than 1, so the terms grow without bound.",
  shrinking:
    "|r| is less than 1, so the terms shrink toward 0 and the infinite series converges.",
  alternating: "r is negative, so the signs alternate.",
  constant: "r is 1, so every term equals the first.",
  zeroRatio: "r is 0, so every term after the first is 0.",
  noInfinite:
    "The infinite series converges only when |r| < 1. With |r| of 1 or more the terms do not shrink, so the sum has no finite value.",
  caseHeading: "Case r = {r}",
  irrational:
    "The ratio is irrational: r ≈ {r} and a₁ ≈ {a1}. Terms of this sequence cannot be written exactly, so enter r as a rounded value in the first mode if you need approximate terms.",
  noRatio:
    "No real ratio fits. A geometric sequence cannot have a zero term next to a nonzero one, and an even number of steps cannot turn a positive term into a negative one.",
  allZero: "Both terms are 0, which fits every ratio. Use a nonzero term.",
  tooLargeRoot:
    "These terms are too far apart in size to take the root of in this calculator.",
  positionFound: "{target} is term number {n}.",
  positionNot:
    "{target} is not a term of this sequence within its first {max} terms.",
  positionEvery:
    "Every term equals {target}, so it is term 1, 2, 3, and so on.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "a{ns} = {an}; S{ns} = {sum}",
  enter: "Fill in every field to see the result.",
  invalid: "Enter numbers, decimals or fractions like 3/4.",
  tooLong: "Use numbers of at most 30 digits.",
  badN: "The term number must be a whole number from 1 to {max}.",
  samePosition: "The two known terms must be at different positions.",
  badPosition: "Positions must be whole numbers from 1 to {max}.",
} as const;

type Strings = typeof GEOMETRIC_SEQUENCE_CALCULATOR_STRINGS;
type Mode = "first" | "two" | "position";
type Show = (value: Rational) => string;

const ONE = BigInt(1);
const MAX_N_BIG = BigInt(MAX_N);
const LISTED_TERMS = 8;
const EXACT_DIGITS_LIMIT = 40;
const SUBSCRIPT_DIGITS = "₀₁₂₃₄₅₆₇₈₉";
const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const PROSE_SM = "text-muted-foreground text-sm";

function subscript(n: number): string {
  return [...String(n)].map((d) => SUBSCRIPT_DIGITS[Number(d)]).join("");
}

function decimalMarkFor(locale: string): string {
  const part = new Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find((p) => p.type === "decimal");
  return part?.value ?? ".";
}

/** A negative or fractional value in brackets, so "· −1/2^(n − 1)" stays readable. */
function bracketed(value: Rational, show: Show): string {
  const text = show(value);
  return text.startsWith("−") || text.includes("/") ? `(${text})` : text;
}

function rationalError(
  reads: readonly RationalRead[],
  strings: Strings
): string | null {
  if (reads.some((r) => r.kind === "too-long")) {
    return strings.tooLong;
  }
  return reads.some((r) => r.kind === "invalid") ? strings.invalid : null;
}

function nature(r: Rational, strings: Strings): string[] {
  const notes: string[] = [];
  const size = compareR(
    { num: r.num < 0 ? -r.num : r.num, den: r.den },
    intR(1)
  );
  if (isZeroR(r)) {
    notes.push(strings.zeroRatio);
  } else if (size === 0 && signR(r) > 0) {
    notes.push(strings.constant);
  } else if (size > 0) {
    notes.push(strings.growing);
  } else if (size < 0) {
    notes.push(strings.shrinking);
  }
  if (signR(r) < 0) {
    notes.push(strings.alternating);
  }
  return notes;
}

function Terms({
  a1,
  r,
  n,
  show,
  strings,
}: {
  a1: Rational;
  r: Rational;
  n: number;
  show: Show;
  strings: Strings;
}) {
  const head = firstTerms(a1, r, Math.min(n, LISTED_TERMS)).map(show);
  const text =
    n > LISTED_TERMS
      ? `${head.join(", ")}, ${strings.termsEllipsis}, ${show(nthTerm(a1, r, n))}`
      : head.join(", ");
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.termsHeading}</h3>
      <p className="break-words rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        {text}
      </p>
    </>
  );
}

function Results({
  a1,
  r,
  n,
  working,
  show,
  strings,
}: {
  a1: Rational;
  r: Rational;
  n: number;
  working: string[];
  show: Show;
  strings: Strings;
}) {
  const an = nthTerm(a1, r, n);
  const sum = partialSum(a1, r, n);
  const inf = infiniteSum(a1, r);
  const rb = bracketed(r, show);
  const values = {
    ns: subscript(n),
    n: String(n),
    a1: show(a1),
    r: show(r),
    rb,
    an: show(an),
    sum: show(sum),
    inf: inf === null ? "" : show(inf),
  };
  const summary = t(strings.summary, values);
  const sumStep = r.num === r.den ? strings.stepSumOne : strings.stepSum;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.nthTermCard, values)}
          value={values.an}
        />
        <StatCard label={t(strings.sumCard, values)} value={values.sum} />
        <StatCard
          label={strings.infiniteCard}
          value={inf === null ? strings.diverges : values.inf}
        />
        <StatCard label={strings.ratioCard} value={values.r} />
        <StatCard label={strings.firstCard} value={values.a1} />
      </StatGrid>
      {nature(r, strings).map((note) => (
        <p className={PROSE_SM} key={note}>
          {note}
        </p>
      ))}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      <h3 className={WIDGET_H3}>{strings.formulaHeading}</h3>
      <div className={MONO_LINES}>
        <p>
          {t(
            equalsR(a1, intR(1))
              ? strings.generalTermUnit
              : strings.generalTerm,
            values
          )}
        </p>
      </div>
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      <div className={MONO_LINES}>
        {working.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{t(strings.stepTerm, values)}</p>
        <p>{t(sumStep, values)}</p>
        {inf === null ? null : <p>{t(strings.stepInfinite, values)}</p>}
      </div>
      {inf === null ? <p className={PROSE_SM}>{strings.noInfinite}</p> : null}
      <Terms a1={a1} n={n} r={r} show={show} strings={strings} />
    </>
  );
}

function FirstMode({
  raw,
  show,
  strings,
}: {
  raw: { a1: string; r: string; n: string };
  show: Show;
  strings: Strings;
}) {
  const a1 = readRationalField(raw.a1);
  const r = readRationalField(raw.r);
  const n = readCountField(raw.n, ONE, MAX_N_BIG);
  const error = rationalError([a1, r], strings);
  if (error) {
    return <ErrorNotice>{error}</ErrorNotice>;
  }
  if (a1.kind !== "ok" || r.kind !== "ok" || n.kind === "empty") {
    return <Notice>{strings.enter}</Notice>;
  }
  if (n.kind !== "ok") {
    return <ErrorNotice>{t(strings.badN, { max: MAX_N })}</ErrorNotice>;
  }
  return (
    <Results
      a1={a1.value}
      n={Number(n.value)}
      r={r.value}
      show={show}
      strings={strings}
      working={[]}
    />
  );
}

function TwoResults({
  fits,
  p,
  q,
  x,
  y,
  n,
  show,
  strings,
}: {
  fits: RatioFit[];
  p: number;
  q: number;
  x: Rational;
  y: Rational;
  n: number;
  show: Show;
  strings: Strings;
}) {
  return (
    <>
      {fits.map((fit) => {
        const values = {
          p: String(p),
          q: String(q),
          ps: subscript(p),
          qs: subscript(q),
          x: show(x),
          y: show(y),
          quotient: show(divR(q > p ? y : x, q > p ? x : y)),
          r: show(fit.r),
          rb: bracketed(fit.r, show),
          a1: show(fit.a1),
        };
        return (
          <div className="space-y-4" key={values.r}>
            {fits.length > 1 ? (
              <h3 className={WIDGET_H3}>{t(strings.caseHeading, values)}</h3>
            ) : null}
            <Results
              a1={fit.a1}
              n={n}
              r={fit.r}
              show={show}
              strings={strings}
              working={[
                t(strings.stepRatio, values),
                t(strings.stepRatioResult, values),
                t(strings.stepFirst, values),
              ]}
            />
          </div>
        );
      })}
    </>
  );
}

function TwoMode({
  fields,
  rawN,
  show,
  strings,
}: {
  fields: { p: string; x: string; q: string; y: string };
  rawN: string;
  show: Show;
  strings: Strings;
}) {
  const p = readCountField(fields.p, ONE, MAX_N_BIG);
  const q = readCountField(fields.q, ONE, MAX_N_BIG);
  const x = readRationalField(fields.x);
  const y = readRationalField(fields.y);
  const n = readCountField(rawN, ONE, MAX_N_BIG);
  const error = rationalError([x, y], strings);
  if (error) {
    return <ErrorNotice>{error}</ErrorNotice>;
  }
  if ([p, q, x, y, n].some((read) => read.kind === "empty")) {
    return <Notice>{strings.enter}</Notice>;
  }
  if (p.kind !== "ok" || q.kind !== "ok") {
    return <ErrorNotice>{t(strings.badPosition, { max: MAX_N })}</ErrorNotice>;
  }
  if (n.kind !== "ok") {
    return <ErrorNotice>{t(strings.badN, { max: MAX_N })}</ErrorNotice>;
  }
  if (x.kind !== "ok" || y.kind !== "ok") {
    return <Notice>{strings.enter}</Notice>;
  }
  const pn = Number(p.value);
  const qn = Number(q.value);
  const fit = fitTwoTerms(pn, x.value, qn, y.value);
  switch (fit.kind) {
    case "ok":
      return (
        <TwoResults
          fits={fit.fits}
          n={Number(n.value)}
          p={pn}
          q={qn}
          show={show}
          strings={strings}
          x={x.value}
          y={y.value}
        />
      );
    case "irrational":
      return (
        <Notice>
          {t(strings.irrational, {
            r: String(Number(fit.r.toPrecision(10))),
            a1: String(Number(fit.a1.toPrecision(10))),
          })}
        </Notice>
      );
    case "same-position":
      return <ErrorNotice>{strings.samePosition}</ErrorNotice>;
    case "all-zero":
      return <ErrorNotice>{strings.allZero}</ErrorNotice>;
    case "too-large":
      return <ErrorNotice>{strings.tooLargeRoot}</ErrorNotice>;
    default:
      return <ErrorNotice>{strings.noRatio}</ErrorNotice>;
  }
}

function PositionMode({
  raw,
  show,
  strings,
}: {
  raw: { a1: string; r: string; target: string };
  show: Show;
  strings: Strings;
}) {
  const a1 = readRationalField(raw.a1);
  const r = readRationalField(raw.r);
  const target = readRationalField(raw.target);
  const error = rationalError([a1, r, target], strings);
  if (error) {
    return <ErrorNotice>{error}</ErrorNotice>;
  }
  if (a1.kind !== "ok" || r.kind !== "ok" || target.kind !== "ok") {
    return <Notice>{strings.enter}</Notice>;
  }
  const found = positionOf(a1.value, r.value, target.value);
  const values = { target: show(target.value), max: String(MAX_N) };
  if (found.kind === "term") {
    return (
      <>
        <StatCard emphasis label={values.target} value={String(found.n)} />
        <p className="text-foreground text-sm">
          {t(strings.positionFound, { ...values, n: String(found.n) })}
        </p>
        <Results
          a1={a1.value}
          n={found.n}
          r={r.value}
          show={show}
          strings={strings}
          working={[]}
        />
      </>
    );
  }
  return (
    <Notice>
      {t(
        found.kind === "every-term"
          ? strings.positionEvery
          : strings.positionNot,
        values
      )}
    </Notice>
  );
}

export default function GeometricSequenceCalculatorWidget() {
  const strings = useToolUi(GEOMETRIC_SEQUENCE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const mark = useMemo(() => decimalMarkFor(locale), [locale]);
  const show: Show = (value) =>
    digitCount(value) > EXACT_DIGITS_LIMIT
      ? scientific(value)
      : formatRationalNice(value, mark);
  const [mode, setMode] = useState<Mode>("first");
  const [a1, setA1] = useState("3");
  const [r, setR] = useState("2");
  const [n, setN] = useState("10");
  const [p, setP] = useState("2");
  const [x, setX] = useState("6");
  const [q, setQ] = useState("5");
  const [y, setY] = useState("162");
  const [target, setTarget] = useState("96");
  const field = (
    id: string,
    label: string,
    value: string,
    onChange: (v: string) => void,
    hint?: string
  ) => (
    <TextField
      hint={hint}
      id={`gsc-${id}`}
      label={label}
      mono
      numeric
      onChange={onChange}
      value={value}
    />
  );
  return (
    <>
      <PillGroup
        label={strings.mode}
        name="gsc-mode"
        onChange={setMode}
        options={[
          { value: "first", label: strings.modeFirst },
          { value: "two", label: strings.modeTwo },
          { value: "position", label: strings.modePosition },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {mode === "two" ? (
          <>
            {field("p", strings.positionP, p, setP)}
            {field("x", strings.valueP, x, setX)}
            {field("q", strings.positionQ, q, setQ)}
            {field("y", strings.valueQ, y, setY)}
          </>
        ) : (
          <>
            {field("a1", strings.firstTerm, a1, setA1)}
            {field("r", strings.ratio, r, setR, strings.ratioHint)}
          </>
        )}
        {mode === "position"
          ? field("target", strings.target, target, setTarget)
          : field(
              "n",
              strings.termNumber,
              n,
              setN,
              t(strings.termNumberHint, { max: MAX_N })
            )}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "first" ? (
          <FirstMode raw={{ a1, r, n }} show={show} strings={strings} />
        ) : null}
        {mode === "two" ? (
          <TwoMode
            fields={{ p, x, q, y }}
            rawN={n}
            show={show}
            strings={strings}
          />
        ) : null}
        {mode === "position" ? (
          <PositionMode raw={{ a1, r, target }} show={show} strings={strings} />
        ) : null}
      </section>
    </>
  );
}
